import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, errorMessage } from '../../common/api/httpClient';
import type { PortalApplication, PortalIdentity } from '../../model/portalModels';
import { myTasksApi } from '../../modules/my-tasks/api/myTasksApi';
import type { PortalMyTaskCounts } from '../../modules/my-tasks/model/myTasks';
import { buildMyTasksRoute } from '../../modules/my-tasks/runtime/myTasksRoute';
import { operationsApi } from '../../modules/operations/api/operationsApi';
import type { PortalOperationFavoriteState, PortalOperationIdentity } from '../../modules/operations/types/portal.types';
import { useMobileApplications } from '../app/mobilePortalEntry';
import type { MobileOperation } from '../types';
import MobileWorkbenchView from '../views/MobileWorkbenchView';
import { applicationModel, operationIdentity, operationModel } from './workbenchModels';

export function MobileWorkbenchPage(props: {
  identity: PortalIdentity; applications: PortalApplication[];
  onNavigate: (path: string) => void; onSessionExpired: () => void;
}) {
  const applications = useMobileApplications();
  const [counts, setCounts] = useState<PortalMyTaskCounts>();
  const [favorites, setFavorites] = useState<PortalOperationFavoriteState>();
  const [operations, setOperations] = useState<MobileOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [configError, setConfigError] = useState<string>();
  const [catalog, setCatalog] = useState<MobileOperation[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogHasMore, setCatalogHasMore] = useState(false);
  const [revision, setRevision] = useState(0);
  const epoch = useRef(0);
  const queryEpoch = useRef(0);
  const query = useRef({ keyword: '', page: 1 });
  const saveInFlight = useRef(false);
  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;
  const report = (failure: unknown) => {
    if (failure instanceof ApiError && failure.status === 401) props.onSessionExpired();
    return errorMessage(failure);
  };
  const readFavorites = useCallback(async () => {
    const currentEpoch = epoch.current;
    const [snapshot, page] = await Promise.all([
      operationsApi.favorites(), operationsApi.list({ favoriteOnly: true, page: 1, pageSize: 20 }),
    ]);
    if (currentEpoch !== epoch.current) return false;
    setFavorites(snapshot);
    favoritesRef.current = snapshot;
    setOperations(current => {
      const merged = new Map(current.map(item => [item.id, item]));
      page.groups.flatMap(group => group.operations).forEach(item => merged.set(operationIdentity(item), operationModel(item)));
      return [...merged.values()];
    });
    return true;
  }, []);

  useEffect(() => {
    const currentEpoch = ++epoch.current;
    setCounts(undefined); setFavorites(undefined); setOperations([]);
    setLoading(true); setError(undefined); setConfigError(undefined);
    void Promise.all([myTasksApi.counts(), readFavorites()]).then(([summary]) => {
      if (currentEpoch === epoch.current) setCounts(summary);
    }).catch(failure => {
      if (currentEpoch === epoch.current) setError(report(failure));
    }).finally(() => { if (currentEpoch === epoch.current) setLoading(false); });
    return () => { epoch.current += 1; queryEpoch.current += 1; };
  }, [props.identity, props.applications, revision, readFavorites]);

  const loadCatalog = async (keyword: string, page: number) => {
    const currentEpoch = epoch.current;
    const request = ++queryEpoch.current;
    query.current = { keyword, page };
    setCatalogLoading(true); setConfigError(undefined);
    if (page === 1) setCatalog([]);
    try {
      const result = await operationsApi.list({ keyword, page, pageSize: 20 });
      if (request !== queryEpoch.current || currentEpoch !== epoch.current) return;
      const items = result.groups.flatMap(group => group.operations).map(operationModel);
      setCatalog(current => page === 1 ? items : [...new Map([...current, ...items].map(item => [item.id, item])).values()]);
      setCatalogHasMore(result.page * result.pageSize < result.total);
      setOperations(current => [...new Map([...current, ...items].map(item => [item.id, item])).values()]);
    } catch (failure) {
      if (request === queryEpoch.current && currentEpoch === epoch.current) {
        setConfigError(report(failure)); setCatalogHasMore(false);
      }
    } finally {
      if (request === queryEpoch.current && currentEpoch === epoch.current) setCatalogLoading(false);
    }
  };

  const saveFavorites = async (ids: string[]) => {
    if (saveInFlight.current || !favoritesRef.current) return false;
    const currentEpoch = epoch.current;
    saveInFlight.current = true;
    let written = false;
    setConfigError(undefined);
    try {
      if (ids.length > 20) throw new Error('最多选择20项常用操作');
      // Draft IDs include the full ordered snapshot, even entries outside the current search page.
      const selected = ids.map(id => {
        const [applicationCode, operationCode] = JSON.parse(id) as [string, string];
        return { applicationCode, operationCode } satisfies PortalOperationIdentity;
      });
      await operationsApi.replaceFavorites({ version: favoritesRef.current.version, operations: selected });
      written = true;
      if (currentEpoch !== epoch.current) return false;
      return await readFavorites();
    } catch (failure) {
      if (currentEpoch === epoch.current) setConfigError(`${written ? '收藏已保存，但回读失败：' : ''}${report(failure)}；已保留选择，请重载服务端收藏后重试。`);
      return false;
    } finally { saveInFlight.current = false; }
  };

  return <MobileWorkbenchView key={`${props.identity.tenantPublicId}:${props.identity.actorPublicId}`} name={props.identity.actorName}
    apps={props.applications.map(applicationModel)} counts={counts}
    loading={loading} error={error} onRetry={() => setRevision(value => value + 1)}
    operations={operations} favoriteIds={favorites?.operations.map(operationIdentity) ?? []}
    catalog={catalog} catalogLoading={catalogLoading} catalogHasMore={catalogHasMore}
    configError={configError} onConfigure={() => void loadCatalog('', 1)}
    onSearch={keyword => void loadCatalog(keyword, 1)}
    onLoadMore={() => void loadCatalog(query.current.keyword, query.current.page + 1)}
    onReloadFavorites={async () => {
      const currentEpoch = epoch.current;
      try { if (await readFavorites()) setConfigError(undefined); }
      catch (failure) { if (currentEpoch === epoch.current) setConfigError(report(failure)); }
    }}
    onFavoriteChange={saveFavorites}
    onOpenTasks={group => props.onNavigate(buildMyTasksRoute(group === 'cc' ? 'cc' : 'todo'))}
    onOpenApps={applications.openSwitcher} onOpenApp={applications.selectApplication}
    onOpenOperation={item => props.onNavigate(`/portal/operations/${encodeURIComponent(item.appCode)}/${encodeURIComponent(item.operationCode)}`)} />;
}
